// Thin wrapper over the @bbpos/ui DataTable that applies the shared report
// table chrome (section title, count pill, card container, hover/shadow) so
// every report page looks identical without repeating boilerplate.

import type { ReactNode } from 'react';
import { DataTable } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';

export interface ReportTableProps<T extends { id: string }> {
  title?: string;
  subtitle?: string;
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  headerActions?: ReactNode;
  stickyHeader?: boolean;
}

export function ReportTable<T extends { id: string }>({
  title,
  subtitle,
  columns,
  data,
  loading,
  emptyTitle = 'No data available',
  emptyDescription,
  totalCount,
  currentPage,
  pageSize = 20,
  onPageChange,
  headerActions,
  stickyHeader = true,
}: ReportTableProps<T>) {
  return (
    <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up mb-6">
      {(title || headerActions) && (
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-neutral-100 dark:border-neutral-700/60 px-5 py-4 bg-gradient-to-r from-neutral-50/70 to-transparent dark:from-neutral-800/80">
          <div className="min-w-0">
            {title ? (
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-brand-500 to-violet-500" />
                {title}
                {typeof totalCount === 'number' ? (
                  <span className="ml-1 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200 px-2 py-0.5 text-xs font-semibold tabular-nums">
                    {totalCount.toLocaleString('en-IN')}
                  </span>
                ) : null}
              </h2>
            ) : null}
            {subtitle ? (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
            ) : null}
          </div>
          {headerActions ? <div className="flex items-center gap-2">{headerActions}</div> : null}
        </header>
      )}
      <DataTable<T>
        columns={columns}
        data={data}
        loading={loading}
        stickyHeader={stickyHeader}
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        emptyState={{
          title: emptyTitle,
          description: emptyDescription,
        }}
      />
    </section>
  );
}
